import { apiService } from "./api";

// Team tasks — staff assigning work to each other. Mirrors
// api/app/schemas/tasks.py.

export type TaskStatus = "TODO" | "IN_PROGRESS" | "WAITING" | "DONE";
export type TaskPriority = "URGENT" | "HIGH" | "NORMAL" | "LOW";
export type TaskRepeat = "DAILY" | "WEEKLY" | "MONTHLY";

export interface Task {
    task_id: string;
    title: string;
    description: string | null;
    status: TaskStatus;
    priority: TaskPriority;
    assignee_user_id: string | null;
    created_by_user_id: string | null;
    completed_by_user_id: string | null;
    /** A calendar date, "YYYY-MM-DD" — never shifted by timezones. */
    due_date: string | null;
    /** "HH:MM:SS" or null. */
    due_time: string | null;
    completed_at: string | null;
    sort_order: number;
    labels: string[];
    order_id: string | null;
    order_number: string | null;
    customer_id: string | null;
    customer_name: string | null;
    product_id: string | null;
    product_name: string | null;
    repeat_rule: TaskRepeat | null;
    repeat_interval: number;
    series_id: string | null;
    checklist_total: number;
    checklist_done: number;
    comment_count: number;
    create_time: string;
    update_time: string;
}

export interface ChecklistItem {
    item_id: string;
    text: string;
    is_done: boolean;
    position: number;
    done_by_user_id: string | null;
    done_at: string | null;
}

export interface TaskComment {
    comment_id: string;
    author_user_id: string | null;
    body: string;
    edited_at: string | null;
    create_time: string;
}

export interface TaskEvent {
    event_id: string;
    actor_user_id: string | null;
    kind: string;
    payload: Record<string, any>;
    create_time: string;
}

export interface TaskDetail extends Task {
    checklist: ChecklistItem[];
    comments: TaskComment[];
    events: TaskEvent[];
}

export interface TaskList {
    tasks: Task[];
    /** Today in the business calendar (Dhaka), "YYYY-MM-DD". */
    today: string;
}

export interface TaskPerson {
    user_id: string;
    email: string;
    display_name: string | null;
    role: string;
    assignable: boolean;
    /** False once deactivated; their name still shows on old tasks. */
    is_active?: boolean;
    open_count: number;
    overdue_count: number;
}

export type TaskNotificationKind = "assigned" | "unassigned" | "comment" | "mention" | "completed";

export interface TaskNotification {
    notification_id: string;
    task_id: string;
    task_title: string;
    kind: TaskNotificationKind | string;
    actor_user_id: string | null;
    read_at: string | null;
    create_time: string;
}

export interface TaskSummary {
    open_assigned: number;
    overdue: number;
    due_today: number;
    unread_notifications: number;
    latest_unread: TaskNotification[];
}

export interface TaskInput {
    title: string;
    description?: string | null;
    status?: TaskStatus;
    priority?: TaskPriority;
    assignee_user_id?: string | null;
    due_date?: string | null;
    due_time?: string | null;
    labels?: string[];
    order_id?: string | null;
    customer_id?: string | null;
    product_id?: string | null;
    repeat_rule?: TaskRepeat | null;
    repeat_interval?: number;
    checklist?: string[];
}

/** Only the fields sent change; null clears an optional one. */
export type TaskChanges = Partial<Omit<TaskInput, "checklist">>;

export interface TaskListQuery {
    status?: TaskStatus[];
    assignee?: string;
    order_id?: string;
    customer_id?: string;
    product_id?: string;
    /** Done tasks from the last N days; -1 = all. */
    done_days?: number;
}

export type BulkAction =
    | { action: "assign"; assignee_user_id: string | null }
    | { action: "status"; status: TaskStatus }
    | { action: "priority"; priority: TaskPriority }
    | { action: "due"; due_date: string | null }
    | { action: "delete" };

const BASE = "/tasks";

/** Fired on window when tasks change on this tab — the header badges re-read. */
export const TASKS_CHANGED_EVENT = "tasks:changed";

export const taskService = {
    list(query: TaskListQuery = {}): Promise<TaskList> {
        const params = new URLSearchParams();
        if (query.status?.length) params.set("status", query.status.join(","));
        if (query.assignee) params.set("assignee", query.assignee);
        if (query.order_id) params.set("order_id", query.order_id);
        if (query.customer_id) params.set("customer_id", query.customer_id);
        if (query.product_id) params.set("product_id", query.product_id);
        if (query.done_days !== undefined) params.set("done_days", String(query.done_days));
        const qs = params.toString();
        return apiService.get<TaskList>(qs ? `${BASE}?${qs}` : BASE);
    },
    get: (taskId: string) => apiService.get<TaskDetail>(`${BASE}/${taskId}`),
    create: (data: TaskInput) => apiService.post<TaskDetail>(BASE, data),
    update: (taskId: string, changes: TaskChanges) => apiService.patch<TaskDetail>(`${BASE}/${taskId}`, changes),
    remove: (taskId: string) => apiService.delete<void>(`${BASE}/${taskId}`),
    move: (taskId: string, status: TaskStatus, beforeTaskId: string | null, afterTaskId: string | null) =>
        apiService.post<Task>(`${BASE}/${taskId}/move`, {
            status,
            before_task_id: beforeTaskId,
            after_task_id: afterTaskId,
        }),
    bulk: (taskIds: string[], action: BulkAction) =>
        apiService.post<{ updated: number }>(`${BASE}/bulk`, { task_ids: taskIds, ...action }),

    addChecklistItem: (taskId: string, text: string) =>
        apiService.post<ChecklistItem>(`${BASE}/${taskId}/checklist`, { text }),
    updateChecklistItem: (itemId: string, changes: { text?: string; is_done?: boolean }) =>
        apiService.patch<ChecklistItem>(`${BASE}/checklist/${itemId}`, changes),
    deleteChecklistItem: (itemId: string) => apiService.delete<void>(`${BASE}/checklist/${itemId}`),
    reorderChecklist: (taskId: string, itemIds: string[]) =>
        apiService.put<ChecklistItem[]>(`${BASE}/${taskId}/checklist/order`, { item_ids: itemIds }),

    addComment: (taskId: string, body: string, mentionUserIds: string[]) =>
        apiService.post<TaskComment>(`${BASE}/${taskId}/comments`, { body, mention_user_ids: mentionUserIds }),
    updateComment: (commentId: string, body: string) =>
        apiService.patch<TaskComment>(`${BASE}/comments/${commentId}`, { body }),
    deleteComment: (commentId: string) => apiService.delete<void>(`${BASE}/comments/${commentId}`),

    people: () => apiService.get<TaskPerson[]>(`${BASE}/people`),
    summary: () => apiService.get<TaskSummary>(`${BASE}/summary`),
    notifications: (limit = 30) => apiService.get<TaskNotification[]>(`${BASE}/notifications?limit=${limit}`),
    markNotificationsRead: (ids: string[] | null) =>
        apiService.post<void>(`${BASE}/notifications/read`, { notification_ids: ids }),
};
